import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n';
import { createProject, saveProjectToWeb } from '../firebase/workspaceService';
import { DEFAULT_GRID_SIZE_CODE } from '../store/houseSlice';
import { loadGuestDesign, clearGuestDesign } from '../utils/guestDesign';
import RegisterForm from './RegisterForm';
import '../pages/Dashboard.css';
import './GuestRegisterModal.css';

// Two-step modal opened by the guest "Save" button (Builder/FloorPlan/
// RoomEditor/House3DView, while on the unauthenticated "/try" flow): sign up
// without leaving the page, then name the project the guest design becomes —
// reuses Dashboard's own "new project" modal markup/i18n for that second step
// so it looks and behaves exactly like the regular create-project flow.
export default function GuestRegisterModal({ onClose }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useT();
  const [step, setStep] = useState('register'); // 'register' | 'name'
  const [projectName, setProjectName] = useState('');
  const [creating, setCreating] = useState(false);
  const [nameError, setNameError] = useState('');

  async function handleCreate() {
    const name = projectName.trim();
    if (!name) { setNameError(t('ws_field_required')); return; }
    setCreating(true);
    try {
      const id = await createProject(user.uid, name, null, DEFAULT_GRID_SIZE_CODE);
      const guestDesign = loadGuestDesign();
      if (guestDesign) {
        await saveProjectToWeb(id, JSON.stringify(guestDesign));
        clearGuestDesign();
      }
      navigate(`/app/${id}`);
    } catch (err) {
      console.error('Failed to create project:', err);
      setCreating(false);
    }
  }

  function handleNameKeyDown(e) {
    if (e.key === 'Enter') handleCreate();
    if (e.key === 'Escape' && !creating) onClose();
  }

  // Portaled straight to <body> — the trigger button lives inside Builder's
  // `position: fixed` sidebar card, and a `position: fixed` overlay nested
  // inside another fixed ancestor gets trapped to that ancestor's box in
  // some browsers instead of the viewport. A portal sidesteps that (and any
  // z-index/overflow clipping from ancestors) entirely.
  return createPortal(
    <div className="grm-overlay" onClick={() => { if (!creating) onClose(); }}>
      {step === 'register' ? (
        <div className="grm-box" onClick={e => e.stopPropagation()}>
          <RegisterForm onSuccess={() => setStep('name')} showSignInLink={false} />
        </div>
      ) : (
        <div className="dash-modal grm-name-box" onClick={e => e.stopPropagation()} onKeyDown={handleNameKeyDown}>
          <h3 className="dash-modal-title">{t('ws_modal_project_title')}</h3>
          <p className="dash-modal-sub">{t('dash_new_project_sub')}</p>
          <input
            className={`dash-modal-input${nameError ? ' dash-modal-input--error' : ''}`}
            placeholder={t('ws_modal_project_ph')}
            value={projectName}
            onChange={e => { setProjectName(e.target.value); setNameError(''); }}
            autoFocus
          />
          {nameError && <p className="dash-modal-error">{nameError}</p>}
          <div className="dash-modal-actions">
            <button
              className="dash-modal-btn dash-modal-btn--primary"
              onClick={handleCreate}
              disabled={creating}
            >
              {creating ? t('ws_creating') : t('ws_create')}
            </button>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
